import { LoadingOutlined, PlusOutlined } from "@ant-design/icons";
import {
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Spin,
  TreeSelect,
} from "antd";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { GetActiveScalesApi } from "../../api/GetActiveScalesApi";
import { GetAllPlanGroupsApi } from "../../api/GetAllPlanGroups";
import GetScaleUnitsByScaleApi from "../../api/GetScaleUnitsByScaleApi";
import GetPlanTemplateByIdApi from "../../api/plan-templates/GetPlanTemplateByIdApi";
import CreateSplitPlanApi from "../../api/plans/CreateSplitPlanApi";
import { GetUnitPositionsApi2 } from "../../api/units/GetUnitPositionsApi2";
import {
  openNotificationError,
  openNotificationSuccess,
} from "../../helpers/NotificationHelpers";
import { GetTreeSelectUnitsApi } from "../../api/GetTreeSelectUnitsApi";


const { Option } = Select;

const { TextArea } = Input;

function SplitPlanModal(props) {
  const [submit, setSubmit] = useState(false);
  const [planLevels, setPlanLevels] = useState([]);
  const [planLevel, setPlanLevel] = useState([]);
  const [allUnitsTree, setAllUnitsTree] = useState([]);
  const [planGroups, setPlanGroups] = useState([]);
  const [scales, setScales] = useState([]);
  const [scaleUnits, setScaleUnits] = useState([]);
  const [percentage, setPercentage] = useState(false);
  const [addScaleUnit, setAddScaleUnit] = useState(false);
  const [unitPositions, setUnitPositions] = useState([]);
  const perfCycleState = useSelector((state) => state.perfCycleState.value);
  const [form] = Form.useForm();

  function handleCancel() {
    setSubmit(false);
    setAllUnitsTree([]);
    setPlanGroups([]);
    setPlanLevels([]);
    setPlanLevel([]);
    setScales([]);
    setScaleUnits([]);
    setUnitPositions([]);
    setPercentage(false);
    setAddScaleUnit(false);
    form.resetFields();
    props.handleCancel();
  }

  useEffect(() => {
    if (props.visible) {
      getPlanGroups();
      getActiveScales();
      getPlanLevels();
      getUnitUsers();
      getAllUnitsTree();
    }
  }, [props.visible]);

  async function getUnitUsers() {
    const apiResponse = await GetUnitPositionsApi2(props.plan?.unit?.unitId);

    if (apiResponse.message === "success") {
      setUnitPositions(apiResponse.data);
    }
  }

  async function onFinish(values) {
    setSubmit(true);
    let attributesValues = [];
    planLevel?.planLevelAttributes?.forEach((attribute) => {
      attributesValues.push({
        attributeId: attribute.id,
        value: values[attribute.id],
      });
    });

    const apiResponse = await CreateSplitPlanApi(
      props.plan?.planId,
      values.targetScale || null,
      addScaleUnit ? values.unitScale || null : null,
      values.title,
      values.planLevel,
      values.description || null,
      values.target || 0,
      values.targetDate || null,
      values.assignTo,
      attributesValues,
      values.unit
    );
    if (apiResponse.statusCode) {
      openNotificationError("bottomRight", apiResponse.message);
    } else if (apiResponse.message === "success") {
      openNotificationSuccess("bottomRight", `Split plan successfully`);
      props.handleOk(apiResponse.data);
    } else {
      openNotificationError(
        "bottomRight",
        "Couldn't split plan. Try again later."
      );
    }
    handleCancel();
  }

  async function getPlanGroups() {
    const apiResponse = await GetAllPlanGroupsApi();
    if (apiResponse.message === "success") {
      setPlanGroups(apiResponse.planGroups);
    }
  }

  async function getPlanLevels() {
    let newPlanLevels = [];
    const apiResponse = await GetPlanTemplateByIdApi(perfCycleState?.template);

    if (apiResponse.success) {
      apiResponse.data?.planLevels?.forEach((level) => {
        if (props.plan.planLevelOrder < level.order) {
          newPlanLevels.push(level);
        }
      });
      setPlanLevels(newPlanLevels);
    }
  }

  async function getActiveScales() {
    const apiResponse = await GetActiveScalesApi();
    if (apiResponse.success) {
      setScales(apiResponse.data);
    }
  }

  function selectPlanGroup(value) {
    planGroups.forEach((group) => {
      if (value === group.id) {
        form.setFields([
          {
            name: "targetScale",
            value: group.scale?.id,
          },
        ]);
        selectScale(group.scale?.id);
      }
    });
  }

  async function selectScale(value) {
    setScaleUnits([]);
    let addUnit = "";
    scales.forEach((scale) => {
      if (scale.id === value) {
        if (scale.name === "Number") {
          setAddScaleUnit(false);
          setPercentage(false);
        } else if (scale.name === "Percentage") {
          setAddScaleUnit(false);
          setPercentage(true);
        } else {
          setAddScaleUnit(true);
          setPercentage(false);
          addUnit = "add";
        }
      }
    });
    if (addUnit === "add") {
      const apiResponse = await GetScaleUnitsByScaleApi(value);
      if (apiResponse.message === "success") {
        setScaleUnits(apiResponse.scaleUnits);
      }
      apiResponse.scaleUnits?.forEach((unit) => {
        if (unit.default) {
          form.setFields([
            {
              name: "unitScale",
              value: unit.id,
            },
          ]);
        }
      });
    }
  }

  async function selectPlanLevel(value) {
    planLevels?.forEach((level) => {
      if (level.id === value) {
        setPlanLevel(level);
      }
    });
  }


  async function getAllUnitsTree() {
    const apiResponse = await GetTreeSelectUnitsApi();
    if (apiResponse.message === "success") {
      setAllUnitsTree(apiResponse.units);
    }
  }
  async function selectUnit(value) {
    const apiResponse = await GetUnitPositionsApi2(value);
    if (apiResponse.success) {
      setUnitPositions(apiResponse.data);
      form.setFields([
        {
          name: "assignTo",
          value: "",
        },
      ]);
    }
  }

  const unitSelector = (
    <Form.Item name="unitScale" noStyle>
      <Select style={{ width: 100 }}>
        {scaleUnits.map((unit) => {
          return (
            <Option key={unit.id} value={unit.id}>
              {unit.abbreviation}
            </Option>
          );
        })}
      </Select>
    </Form.Item>
  );

  function renderDropdownAndBooleanAttributes() {
    let output = [];

    // Only render dropdown and boolean attributes
    const dropdownAndBooleanAttributes = planLevel?.planLevelAttributes?.filter((attribute) => {
      return attribute.valueType === "dropdown" || attribute.valueType === "boolean";
    }) || [];

    dropdownAndBooleanAttributes.forEach((attribute) => {
      output.push(
        <Col span={12} key={attribute.id}>
          <Form.Item label={attribute.name} name={attribute.id}>
            {attribute.valueType === "dropdown" ? (
              <Select
                mode="multiple"
                showSearch
                allowClear
                placeholder={`Select ${attribute.name}`}
                style={{ width: "100%" }}
                getPopupContainer={(trigger) => trigger.parentNode}
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
              >
                {attribute.dropdownOptions?.map((option) => (
                  <Option key={option.id} value={option.id}>
                    {option.value}
                  </Option>
                ))}
              </Select>
            ) : attribute.valueType === "boolean" ? (
              <Select
                placeholder={`Select ${attribute.name}`}
                style={{ width: "100%" }}
                getPopupContainer={(trigger) => trigger.parentNode}
              >
                <Option value={true}>Yes</Option>
                <Option value={false}>No</Option>
              </Select>
            ) : null}
          </Form.Item>
        </Col>
      );
    });

    return output;
  }

  function renderOtherAttributes() {
    let output = [];

    // Filter out dropdown, boolean, and date attributes (all date attributes handled separately)
    const otherAttributes = planLevel?.planLevelAttributes?.filter((attribute) => {
      return !(attribute.valueType === "dropdown" || 
               attribute.valueType === "boolean" || 
               attribute.valueType === "date");
    }) || [];

    otherAttributes.forEach((attribute) => {
      // Only render supported attribute types to avoid empty form items
      if (attribute.valueType === "text" || attribute.valueType === "number" || attribute.valueType === "double") {
        output.push(
          <Col span={12} key={attribute.id}>
            <Form.Item label={attribute.name} name={attribute.id}>
              {attribute.valueType === "text" ? (
                <Input style={{ width: "100%" }} placeholder={attribute.name} />
              ) : attribute.valueType === "number" ? (
                <InputNumber
                  min={0}
                  style={{ width: "100%" }}
                  placeholder={attribute.name}
                />
              ) : attribute.valueType === "double" ? (
                <InputNumber
                  min={0}
                  step={0.01}
                  style={{ width: "100%" }}
                  placeholder={attribute.name}
                />
              ) : null}
            </Form.Item>
          </Col>
        );
      }
    });

    return output;
  }



  return (
    <>
      <Modal
        title={`Split Plan`}
        visible={props.visible}
        onCancel={() => handleCancel()}
        footer={null}
        destroyOnClose={true}
        width={900}
      >
        <div className="mb-2 text-base">{`Split from: ${props?.plan?.title}`}</div>
        <Form
          name="splitPlan"
          onFinish={onFinish}
          autoComplete="off"
          layout="vertical"
          requiredMark={false}
          form={form}
          initialValues={{ planGroup: props.plan?.planGroup }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Plan Group*" name="planGroup">
                <Input disabled placeholder="Plan group" />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label="Plan Level"
                name="planLevel"
                rules={[
                  {
                    required: true,
                    message: `Please select plan level!`,
                  },
                ]}
              >
                <Select
                  showSearch
                  placeholder="Select plan level"
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option.children
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                  filterSort={(optionA, optionB) =>
                    optionA.children
                      .toLowerCase()
                      .localeCompare(optionB.children.toLowerCase())
                  }
                  onSelect={(option) => selectPlanLevel(option)}
                >
                  {planLevels?.map((level) => {
                    return (
                      <Option key={level.id} value={level.id}>
                        {level.name}
                      </Option>
                    );
                  })}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Unit*"
                name="unit"
                rules={[
                  {
                    required: true,
                    message: "Please select a unit!",
                  },
                ]}
              >
                <TreeSelect
                  showSearch
                  treeLine={true}
                  dropdownStyle={{
                    maxHeight: 400,
                    overflow: "auto",
                  }}
                  treeData={allUnitsTree}
                  placeholder="Select unit"
                  treeDefaultExpandAll
                  treeNodeFilterProp={"title"}
                  showIcon={false}
                  filterTreeNode
                  onChange={(value) => selectUnit(value)}
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label="Assign to*"
                name="assignTo"
                rules={[
                  {
                    required: true,
                    message: "Please select a position!",
                  },
                ]}
              >
                <Select
                  showSearch
                  placeholder="Select position"
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option.children
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                  filterSort={(optionA, optionB) =>
                    optionA.children
                      .toLowerCase()
                      .localeCompare(optionB.children.toLowerCase())
                  }
                >
                  {unitPositions.map((position) => {
                    return (
                      <Option key={position.id} value={position.id}>
                        {(position.user?.firstName
                          ? position.user?.firstName + ", "
                          : "") + position.designation?.name}
                      </Option>
                    );
                  })}
                </Select>
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label={`Title*`}
                name="title"
                rules={[
                  {
                    required: true,
                    message: `Please enter title!`,
                  },
                ]}
              >
                <Input placeholder={"title"} />
              </Form.Item>
            </Col>


            {planLevel.target && (
              <>
                <Col span={12}>
                  <Form.Item
                    label="Target scale*"
                    name="targetScale"
                    rules={[
                      {
                        required: true,
                        message: "Please select a target scale!",
                      },
                    ]}
                  >
                    <Select
                      showSearch
                      placeholder="Select a scale"
                      optionFilterProp="children"
                      filterOption={(input, option) =>
                        option.children
                          .toLowerCase()
                          .indexOf(input.toLowerCase()) >= 0
                      }
                      filterSort={(optionA, optionB) =>
                        optionA.children
                          .toLowerCase()
                          .localeCompare(optionB.children.toLowerCase())
                      }
                      onChange={(value) => selectScale(value)}
                    >
                      {scales.map((scale) => {
                        return (
                          <Option key={scale.id} value={scale.id}>
                            {scale.name}
                          </Option>
                        );
                      })}
                    </Select>
                  </Form.Item>
                </Col>

                <Col span={12}>
                  <Form.Item
                    label="Target*"
                    name="target"
                    rules={[
                      {
                        required: true,
                        message: "Please enter target!",
                      },
                    ]}
                  >
                    <InputNumber
                      min={0}
                      addonAfter={
                        addScaleUnit ? unitSelector : percentage && "%"
                      }
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                </Col>
              </>
            )}

            {planLevel.description && (
              <Col span={24}>
                <Form.Item label="Description" name="description">
                  <TextArea
                    showCount
                    maxLength={600}
                    placeholder="Description"
                  />
                </Form.Item>
              </Col>
            )}

            {renderDropdownAndBooleanAttributes()}

            {planLevel.targetDate && (
              <Col span={12}>
                <Form.Item label="Target date*" name="targetDate">
                  <DatePicker className="w-full" placeholder="Target date" />
                </Form.Item>
              </Col>
            )}

            {renderOtherAttributes()}

            <Col span={24}>
              <Form.Item style={{ marginBottom: 0 }}>
                <div className=" flex justify-end">
                  <Button className="mr-3 w-28" onClick={() => handleCancel()}>
                    Cancel
                  </Button>
                  <Button
                    className="btn w-32"
                    type="primary"
                    htmlType={submit && "submit"}
                    disabled={submit}
                  >
                    {submit ? (
                      <Spin
                        size="small"
                        indicator={
                          <LoadingOutlined style={{ color: "#fff" }} spin />
                        }
                      />
                    ) : (
                      `Split`
                    )}
                  </Button>
                </div>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
}

export default SplitPlanModal;
